import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { DropdownScrollArea } from "./DropdownScrollArea";
import {
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "@/components/icons";
import { filterButtonVariants } from "./FilterButton";
import {
  MONTH_OPTIONS,
  WEEKDAYS,
  buildCalendarCells,
  formatDateOnly,
  getYearOptions,
  isDateInRange,
  isSameDay,
  isToday,
  useCalendarView,
  useScrollToSelectedItem,
  type DayStatus,
} from "./calendarCore";

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

export interface QuickRangeOption {
  label: string;
  getValue: () => DateRange;
}

export interface LabelOption {
  value: string;
  label: string;
}

export interface DateRangePickerProps {
  /** 필드 이름 (폼 연동용) */
  name?: string;
  /** 필터 라벨 */
  label?: string;
  /** 라벨 선택 옵션 (드롭다운으로 표시) */
  labelOptions?: LabelOption[];
  /** 선택된 라벨 값 */
  labelValue?: string;
  /** 라벨 값 변경 콜백 */
  onLabelChange?: (value: string) => void;
  placeholder?: string;
  placeholderStyle?: string;
  /** 선택된 날짜 범위 */
  value?: DateRange;
  /** 날짜 범위 변경 콜백 */
  onChange?: (range: DateRange) => void;
  /** 캘린더 아이콘 표시 여부 */
  showCalendarIcon?: boolean;
  /** 클리어 버튼 표시 여부 */
  showClearButton?: boolean;
  /** 캘린더 화살표 아이콘 표시 여부 */
  showArrowIcon?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 추가 className */
  className?: string;
  /** 빠른 선택 옵션 표시 여부 */
  showQuickOptions?: boolean;
  /** 커스텀 빠른 선택 옵션 */
  quickOptions?: QuickRangeOption[];
  /** 날짜 표시 포맷 함수 */
  formatDisplayDate?: (date: Date) => string;
  /** 선택 가능한 최소 날짜 */
  minDate?: Date;
  /** 선택 가능한 최대 날짜 */
  maxDate?: Date;
  /** 팝오버 열림 상태 (제어형) */
  open?: boolean;
  /** 팝오버 열림 상태 변경 콜백 (제어형) */
  onOpenChange?: (open: boolean) => void;
}

const DEFAULT_QUICK_RANGE_OPTIONS: QuickRangeOption[] = [
  {
    label: "오늘",
    getValue: () => {
      const today = new Date();
      return { startDate: today, endDate: today };
    },
  },
  {
    label: "지난 1주일",
    getValue: () => {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 7);
      return { startDate, endDate: today };
    },
  },
  {
    label: "지난 1개월",
    getValue: () => {
      const today = new Date();
      const startDate = new Date();
      startDate.setMonth(today.getMonth() - 1);
      return { startDate, endDate: today };
    },
  },
  {
    label: "지난 3개월",
    getValue: () => {
      const today = new Date();
      const startDate = new Date();
      startDate.setMonth(today.getMonth() - 3);
      return { startDate, endDate: today };
    },
  },
];

interface DayCellProps {
  day: number;
  status: DayStatus;
  isCurrentMonth: boolean;
  isDisabled?: boolean;
  onClick: () => void;
}

function DayCell({
  day,
  status,
  isCurrentMonth,
  isDisabled,
  onClick,
}: DayCellProps) {
  const isRangeStart = status === "rangeStart";
  const isRangeEnd = status === "rangeEnd";
  const isSelected = status === "selected";
  const isInRange = status === "inRange";
  const isInRangeArea = isRangeStart || isRangeEnd || isInRange;
  const disabled = !isCurrentMonth || isDisabled;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "relative h-8 w-full flex items-center justify-center",
        "typo-label-1-medium transition-colors",
        "focus:outline-none",
        disabled && "cursor-default",
        isDisabled && isCurrentMonth && "text-labels-assistive",
        !disabled && !isInRangeArea && !isSelected && "hover:bg-fills-primary",
        !isDisabled && status === "normal" && "text-labels-primary",
        !isDisabled && status === "today" && "text-primary-normal",
        status === "assistive" && "text-labels-assistive",
        isSelected && "text-static-white",
        (isRangeStart || isRangeEnd) && "text-static-white",
        isInRange && "text-labels-primary",
      )}
    >
      {/* Range background - extends full width for continuous band */}
      {isInRangeArea && (
        <div
          className={cn(
            "absolute inset-y-0 bg-primary-normal/10",
            isRangeStart && "left-1/2 right-0",
            isRangeEnd && "left-0 right-1/2",
            isInRange && "inset-x-0",
          )}
        />
      )}
      {/* Selected/Range edge indicator - centered 30px box */}
      {(isSelected || isRangeStart || isRangeEnd) && (
        <div
          className={cn(
            "absolute top-0 size-7.5 left-1/2 -translate-x-1/2 bg-primary-normal",
            isSelected && "rounded-lg",
            isRangeStart && "rounded-l-lg",
            isRangeEnd && "rounded-r-lg",
          )}
        >
          {/* Inner shadow */}
          <div className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_0px_0px_1px_rgba(43,45,47,0.1),inset_0px_-1px_0px_0px_rgba(43,45,47,0.1)]" />
        </div>
      )}
      {/* Day number */}
      <span className="relative z-10">{day}</span>
    </button>
  );
}

function DateRangePicker({
  name,
  label,
  labelOptions,
  labelValue,
  onLabelChange,
  placeholder,
  placeholderStyle,
  value = { startDate: null, endDate: null },
  onChange,
  showCalendarIcon = true,
  showClearButton = true,
  showArrowIcon = true,
  disabled = false,
  className,
  showQuickOptions = true,
  quickOptions = DEFAULT_QUICK_RANGE_OPTIONS,
  formatDisplayDate = formatDateOnly,
  minDate,
  maxDate,
  open: openProp,
  onOpenChange,
}: DateRangePickerProps) {
  const [openState, setOpenState] = React.useState(false);
  const open = openProp ?? openState;
  const setOpen = React.useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const {
    viewYear,
    viewMonth,
    setViewYear,
    setViewMonth,
    focus,
    goPrevMonth,
    goNextMonth,
  } = useCalendarView(value.startDate);
  const [yearSelectOpen, setYearSelectOpen] = React.useState(false);
  const [monthSelectOpen, setMonthSelectOpen] = React.useState(false);
  const [labelSelectOpen, setLabelSelectOpen] = React.useState(false);
  const yearListRef = React.useRef<HTMLDivElement>(null);
  const monthListRef = React.useRef<HTMLDivElement>(null);

  // 범위 선택 상태 (첫 번째 클릭 후 두 번째 클릭 대기)
  const [selectingStart, setSelectingStart] = React.useState<Date | null>(null);

  useScrollToSelectedItem(yearSelectOpen, yearListRef);
  useScrollToSelectedItem(monthSelectOpen, monthListRef);

  const isSelected = value.startDate !== null || value.endDate !== null;

  // labelOptions가 있을 때 현재 선택된 라벨 찾기
  const currentLabelOption = labelOptions?.find(
    (opt) => opt.value === labelValue,
  );
  const displayLabel = currentLabelOption?.label ?? label;

  // 열리는 순간 시작일(없으면 오늘) 기준으로 달력을 맞추고 선택 중이던 범위를 버린다.
  //
  // 트리거 클릭 외에 부모가 `open` 을 직접 켜는 제어형 사용처가 있어(매출 집계의
  // '직접 설정') 열기 핸들러가 아니라 열림 전환을 렌더에서 감지한다.
  // effect 로 하면 열릴 때마다 렌더가 한 번 더 돈다
  const [wasOpen, setWasOpen] = React.useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      focus(value.startDate);
      setSelectingStart(null);
    }
  }

  const handleClearClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen(false);
    onChange?.({ startDate: null, endDate: null });
  };

  const handleDateSelect = (day: number, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) return;

    const selectedDate = new Date(viewYear, viewMonth, day);
    if (isDateDisabled(selectedDate)) return;

    if (selectingStart === null) {
      // 첫 번째 날짜 선택
      setSelectingStart(selectedDate);
    } else {
      // 두 번째 날짜 선택 - 범위 완성
      let startDate = selectingStart;
      let endDate = selectedDate;

      // 시작일이 종료일보다 늦으면 swap
      if (startDate.getTime() > endDate.getTime()) {
        [startDate, endDate] = [endDate, startDate];
      }

      onChange?.({ startDate, endDate });
      setSelectingStart(null);
      setOpen(false);
    }
  };

  const handleQuickOptionClick = (option: QuickRangeOption) => {
    const range = option.getValue();
    onChange?.(range);
    setSelectingStart(null);
    setOpen(false);
  };

  const isDateDisabled = (date: Date): boolean => {
    if (minDate) {
      const min = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        minDate.getDate(),
      );
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (d < min) return true;
    }
    if (maxDate) {
      const max = new Date(
        maxDate.getFullYear(),
        maxDate.getMonth(),
        maxDate.getDate(),
      );
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (d > max) return true;
    }
    return false;
  };

  const dayStatus = (date: Date): DayStatus => {
    // 첫 클릭만 한 상태 — 시작일 표시만 남긴다
    if (selectingStart && isSameDay(date, selectingStart)) return "rangeStart";
    // 시작·종료가 같은 하루짜리 범위
    if (isSameDay(date, value.startDate) && isSameDay(date, value.endDate)) {
      return "selected";
    }
    if (isSameDay(date, value.startDate)) return "rangeStart";
    if (isSameDay(date, value.endDate)) return "rangeEnd";
    if (isDateInRange(date, value.startDate, value.endDate)) return "inRange";
    if (isToday(date)) return "today";
    return "normal";
  };

  const calendarDays = buildCalendarCells(viewYear, viewMonth, dayStatus).map(
    (cell) => ({
      ...cell,
      // min·max 제한은 이번 달 날짜에만 의미가 있다 (이웃 달 칸은 클릭 자체가 막혀 있다)
      isDisabled:
        cell.isCurrentMonth &&
        isDateDisabled(new Date(viewYear, viewMonth, cell.day)),
    }),
  );

  const yearOptions = getYearOptions();
  const monthOptions = MONTH_OPTIONS;

  // 선택된 값 표시 텍스트
  const getDisplayText = () => {
    if (!value.startDate && !value.endDate) return null;

    if (value.startDate && value.endDate) {
      return `${formatDisplayDate(value.startDate)} ~ ${formatDisplayDate(value.endDate)}`;
    }

    if (value.startDate) {
      return formatDisplayDate(value.startDate);
    }

    return null;
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          name={name}
          disabled={disabled}
          className={cn(
            filterButtonVariants({
              status: isSelected && !disabled ? "selected" : "form",
              disabled,
            }),
            "group",
            className,
          )}
        >
          {/* Contents */}
          <div
            className={cn("flex items-center gap-1", disabled && "opacity-40")}
          >
            {/* Calendar Icon */}
            {showCalendarIcon && (
              <Calendar
                size={16}
                color={
                  isSelected && !disabled
                    ? "primary-normal"
                    : "labels-alternative"
                }
              />
            )}

            {/* Label */}
            {labelOptions && labelOptions.length > 0 ? (
              <PopoverPrimitive.Root
                open={labelSelectOpen}
                onOpenChange={setLabelSelectOpen}
              >
                <PopoverPrimitive.Trigger asChild>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className={cn(
                      "flex items-center gap-0.5 typo-label-1-semibold whitespace-nowrap",
                      isSelected && !disabled
                        ? "text-primary-normal"
                        : "text-labels-alternative",
                      "hover:text-primary-normal",
                    )}
                  >
                    {displayLabel}
                    <ChevronDown size={14} color="labels-alternative" />
                  </button>
                </PopoverPrimitive.Trigger>
                <PopoverPrimitive.Portal>
                  <PopoverPrimitive.Content
                    className={cn(
                      "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                      "z-100 overflow-hidden",
                    )}
                    sideOffset={4}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownScrollArea contentClassName="p-1">
                      {labelOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onLabelChange?.(option.value);
                            setLabelSelectOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                            "hover:bg-fills-primary",
                            option.value === labelValue &&
                              "text-primary-normal bg-fills-highlight",
                          )}
                        >
                          {option.label}
                        </button>
                      ))}
                    </DropdownScrollArea>
                  </PopoverPrimitive.Content>
                </PopoverPrimitive.Portal>
              </PopoverPrimitive.Root>
            ) : (
              label && (
                <span
                  className={cn(
                    "typo-label-1-semibold whitespace-nowrap",
                    isSelected && !disabled
                      ? "text-primary-normal"
                      : "text-labels-alternative",
                  )}
                >
                  {label}
                </span>
              )
            )}

            {placeholder && (
              <span className={cn(placeholderStyle && placeholderStyle)}>
                {placeholder}
              </span>
            )}

            {/* Selected Value */}
            {isSelected && (
              <span
                className={cn(
                  "typo-label-1-semibold whitespace-nowrap",
                  disabled ? "text-labels-alternative" : "text-primary-strong",
                )}
              >
                {getDisplayText()}
              </span>
            )}

            {/* Right Icon */}
            {isSelected && showClearButton ? (
              <span
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={disabled ? undefined : handleClearClick}
                onPointerDown={(e) => {
                  if (disabled) return;
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    e.preventDefault();
                    handleClearClick(e as unknown as React.MouseEvent);
                  }
                }}
                className={cn(
                  "flex items-center justify-center",
                  disabled ? "cursor-not-allowed" : "cursor-pointer",
                )}
                aria-label="날짜 선택 해제"
              >
                <X size={16} color="labels-alternative" />
              </span>
            ) : showArrowIcon ? (
              <ChevronDown size={16} color="labels-alternative" />
            ) : null}
          </div>

          {/* Dim overlay for hover/pressed states */}
          {!disabled && (
            <div
              className={cn(
                "absolute -inset-px pointer-events-none transition-opacity rounded-md",
                "bg-static-black",
                "opacity-0 group-hover:opacity-[0.02] group-active:opacity-10",
              )}
            />
          )}
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            "bg-static-white text-labels-primary",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            "z-50 overflow-hidden rounded-xl",
            "inset-border-solid-normal shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
          )}
          sideOffset={4}
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          {/* 빠른 선택 옵션 */}
          {showQuickOptions && (
            <div className="border-b border-lines-solid-normal px-2 py-3">
              <div className="flex flex-col gap-1">
                {quickOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleQuickOptionClick(option)}
                    className={cn(
                      "flex w-56 items-center h-8 px-2 py-1 rounded-lg",
                      "typo-label-1-semibold text-labels-primary text-left",
                      "cursor-pointer outline-none select-none",
                      "hover:bg-fills-primary focus:bg-fills-primary",
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 캘린더 */}
          <div className="px-2 py-3">
            {/* 헤더: 년/월 선택 및 네비게이션 */}
            <div className="flex items-center justify-between px-1.5 mb-2.5">
              <div className="flex items-center gap-1">
                {/* 연도 선택 */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setYearSelectOpen(!yearSelectOpen);
                      setMonthSelectOpen(false);
                    }}
                    className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                  >
                    {viewYear}년
                    <ChevronDown size={16} color="labels-primary" />
                  </button>
                  {yearSelectOpen && (
                    <DropdownScrollArea
                      scrollerRef={yearListRef}
                      className={cn(
                        "absolute top-full left-0 mt-1",
                        "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                        "z-100 max-h-48",
                      )}
                      contentClassName="p-1"
                    >
                      {yearOptions.map((year) => (
                        <button
                          key={year}
                          type="button"
                          data-selected={year === viewYear}
                          onClick={() => {
                            setViewYear(year);
                            setYearSelectOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                            "hover:bg-fills-primary",
                            year === viewYear &&
                              "text-primary-normal bg-fills-highlight",
                          )}
                        >
                          {year}년
                        </button>
                      ))}
                    </DropdownScrollArea>
                  )}
                </div>

                {/* 월 선택 */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setMonthSelectOpen(!monthSelectOpen);
                      setYearSelectOpen(false);
                    }}
                    className="flex items-center typo-label-1-semibold text-labels-primary hover:text-primary-normal"
                  >
                    {viewMonth + 1}월
                    <ChevronDown size={16} color="labels-primary" />
                  </button>
                  {monthSelectOpen && (
                    <DropdownScrollArea
                      scrollerRef={monthListRef}
                      className={cn(
                        "absolute top-full left-0 mt-1",
                        "bg-static-white inset-border-solid-normal rounded-lg shadow-[0px_4px_16px_0px_rgba(0,0,0,0.05)]",
                        "z-100 max-h-48",
                      )}
                      contentClassName="p-1"
                    >
                      {monthOptions.map((month) => (
                        <button
                          key={month}
                          type="button"
                          data-selected={month === viewMonth}
                          onClick={() => {
                            setViewMonth(month);
                            setMonthSelectOpen(false);
                          }}
                          className={cn(
                            "w-full px-3 py-1.5 rounded text-left typo-label-1-medium whitespace-nowrap",
                            "hover:bg-fills-primary",
                            month === viewMonth &&
                              "text-primary-normal bg-fills-highlight",
                          )}
                        >
                          {month + 1}월
                        </button>
                      ))}
                    </DropdownScrollArea>
                  )}
                </div>
              </div>

              {/* 이전/다음 월 버튼 */}
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                >
                  <ChevronLeft size={20} color="labels-primary" />
                </button>
                <button
                  type="button"
                  onClick={goNextMonth}
                  className="size-5 flex items-center justify-center hover:bg-fills-primary rounded"
                >
                  <ChevronRight size={20} color="labels-primary" />
                </button>
              </div>
            </div>

            {/* 선택 안내 메시지 */}
            {selectingStart && (
              <div className="px-1.5 mb-2 typo-caption-1-medium text-labels-alternative">
                종료일을 선택하세요
              </div>
            )}

            {/* 요일 헤더 */}
            <div className="grid grid-cols-7">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="size-8 flex items-center justify-center typo-label-1-medium text-labels-alternative"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7">
              {calendarDays.map((dayInfo, index) => (
                <DayCell
                  key={index}
                  day={dayInfo.day}
                  status={dayInfo.status}
                  isCurrentMonth={dayInfo.isCurrentMonth}
                  isDisabled={dayInfo.isDisabled}
                  onClick={() =>
                    handleDateSelect(dayInfo.day, dayInfo.isCurrentMonth)
                  }
                />
              ))}
            </div>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker, DEFAULT_QUICK_RANGE_OPTIONS };
